import { create } from 'zustand';

interface AppModalViewState {
    currentApp?: {
        id: string;
        name: string;
        url: string;
    };
    appFrame?: HTMLIFrameElement;
    openApp: (
        url: string,
        title?: string,
        onClose?: () => void,
        onEvent?: (data?: any, event?: MessageEvent) => void
    ) => void;
    eventListener?: any;
    onClose?: () => void;
    onEvent?: (data?: any, event?: MessageEvent) => void;
    closeApp: () => void;
    setAppFrame: (frame: HTMLIFrameElement) => void;
}

const getDomain = (url: string) => {
    try {
        var domain = new URL(url).hostname;
        return domain;
    } catch (e) {
        return null;
    }
}

export const isFromModalFrame = (
    event: Pick<MessageEvent, 'source' | 'origin'>,
    frame: HTMLIFrameElement | undefined,
    domain: string
): boolean => {
    if (!frame?.contentWindow || event.source !== frame.contentWindow) {
        return false;
    }
    let hostname: string;
    try {
        hostname = new URL(event.origin).hostname.toLowerCase();
    } catch (e) {
        return false;
    }
    const expected = domain.toLowerCase();
    return hostname === expected || hostname.endsWith('.' + expected);
};

export const useAppModalViewStore = create<AppModalViewState>()((set, get) => ({
    currentApp: undefined,
    appFrame: undefined,
    extendedView: false,
    showExtendedViewTitle: false,
    pageStack: [],
    onClose: undefined,
    onEvent: undefined,
    eventListener: undefined,
    openApp: (
        url: string,
        title?: string,
        onClose?: () => void,
        onEvent?: (data?: any, event?: MessageEvent) => void
    ) => {
        const domain = getDomain(url);
        if (!domain) {
            return;
        }

        set({
            currentApp: {
                id: url,
                name: title || "",
                url: url,
            },
            onClose,
            onEvent,
        });

        // Only the modal's own frame on the booking page's host (or a
        // subdomain of it) may report a booking: a substring match also let
        // `calendly.com.evil.example` (or any other frame) mark it booked.
        const messageEventListener = (event: MessageEvent) => {
            if (!onEvent || !isFromModalFrame(event, get().appFrame, domain)) {
                return;
            }
            onEvent(event.data, event);
        };
        set({ eventListener: messageEventListener });
        window.addEventListener('message', messageEventListener);
    },
    closeApp: () => {
        const { eventListener, onClose } = get();
        if (eventListener) {
            window.removeEventListener('message', eventListener);
        }
        if (onClose) {
            onClose();
        }
        set({ currentApp: undefined, onClose: undefined, onEvent: undefined, eventListener: undefined });
    },
    setAppFrame: (frame: HTMLIFrameElement) => {
        set({
            appFrame: frame,
        });
    },
}));
